import React, { useState, useRef, useEffect } from 'react';
import Hls from 'hls.js';
import { Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

// URL streaming video Rete Italia
const videoStreamUrl = 'https://d3k8wzt41aflvx.cloudfront.net/RETEITALIATV/Live.m3u8';

const VideoPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const sourceReadyRef = useRef(false);

  // Keep the play/pause UI in sync with the actual <video> state (e.g. if the
  // live stream stalls or ends), and tear down hls.js on unmount.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => { setIsPlaying(true); setError(null); };
    const onPause = () => setIsPlaying(false);
    const onError = () => {
      const e = video.error;
      setError(`Errore video${e ? ` (codice ${e.code})` : ''}${e && e.message ? `: ${e.message}` : ''}`);
    };
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onPause);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onPause);
      video.removeEventListener('error', onError);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  // Attach the HLS source on first play (keeps preload="none" behaviour — nothing
  // is fetched until the user hits play). Safari/iOS play .m3u8 natively; other
  // browsers (Chrome, Edge, Firefox) need hls.js.
  const tryPlay = () => {
    const p = videoRef.current && videoRef.current.play();
    if (p && p.catch) p.catch(() => {});
  };

  const ensureSource = () => {
    const video = videoRef.current;
    if (!video || sourceReadyRef.current) return;
    setError(null);
    // Prefer hls.js where supported (Chrome, Edge, Firefox). Only fall back to
    // native HLS on Safari/iOS, where Hls.isSupported() is false. Checking native
    // first is unreliable — some Chromium browsers report canPlayType for HLS as
    // "maybe" yet can't actually play it.
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      let netRetries = 0;
      let mediaRetries = 0;
      hls.on(Hls.Events.MANIFEST_PARSED, tryPlay); // play as soon as it's ready
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        // Recover from transient blips, but give up (and report) after a few tries
        // so the failure is visible instead of looping silently.
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && netRetries < 3) {
          netRetries += 1;
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRetries < 2) {
          mediaRetries += 1;
          hls.recoverMediaError();
        } else {
          setError(`Streaming non disponibile (${data.type} / ${data.details})`);
          hls.destroy();
          hlsRef.current = null;
          sourceReadyRef.current = false;
        }
      });
      hls.loadSource(videoStreamUrl);
      hls.attachMedia(video);
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoStreamUrl;
    } else {
      setError('Il tuo browser non supporta lo streaming HLS.');
      return;
    }
    sourceReadyRef.current = true;
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      ensureSource();
      tryPlay();
    }
  };

  const retry = () => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    sourceReadyRef.current = false;
    setError(null);
    ensureSource();
    tryPlay();
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    if (videoRef.current) {
      videoRef.current.volume = value[0] / 100;
    }
    if (isMuted && value[0] > 0) {
      setIsMuted(false);
      if (videoRef.current) videoRef.current.muted = false;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
      {/* Video Container */}
      <div className="relative bg-black aspect-video">
        <video
          ref={videoRef}
          className="w-full h-full"
          playsInline
          preload="none"
        >
          Il tuo browser non supporta il tag video.
        </video>

        {/* Live Badge */}
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-[#00247D] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            LIVE VIDEO
          </div>
        </div>

        {/* Play Overlay */}
        {!isPlaying && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Button
              onClick={togglePlay}
              size="lg"
              className="w-20 h-20 rounded-full bg-white/90 hover:bg-white shadow-2xl hover:scale-110 transition-all duration-300"
            >
              <Play className="w-10 h-10 text-[#00247D] fill-[#00247D] ml-1" />
            </Button>
          </div>
        )}

        {/* Error Overlay */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-center px-4">
            <p className="text-white text-sm md:text-base max-w-md">{error}</p>
            <Button onClick={retry} className="bg-[#009246] hover:bg-[#007a3d] text-white">
              Riprova
            </Button>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="flex items-center gap-3">
          {/* Play/Pause */}
          <Button
            onClick={togglePlay}
            size="icon"
            variant="ghost"
            className="hover:bg-gray-200 transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </Button>

          {/* Volume */}
          <Button
            onClick={toggleMute}
            variant="ghost"
            size="icon"
            className="hover:bg-gray-200 transition-colors"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </Button>

          <div className="flex-1 max-w-xs">
            <Slider
              value={[volume]}
              onValueChange={handleVolumeChange}
              max={100}
              step={1}
              className="cursor-pointer"
            />
          </div>

          {/* Fullscreen */}
          <Button
            onClick={toggleFullscreen}
            variant="ghost"
            size="icon"
            className="hover:bg-gray-200 transition-colors ml-auto"
          >
            <Maximize className="w-5 h-5" />
          </Button>
        </div>

        <div className="mt-3 text-center">
          <p className="text-sm text-gray-600 font-medium">📺 Video Streaming Rete Italia</p>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;